import { z } from 'zod';
/**
 * Money handling.
 *
 * Every amount that crosses a boundary — database, API, UI — is an integer
 * count of the smallest unit of its currency. Never a float, never a Decimal:
 * `0.1 + 0.2 !== 0.3`, and a rounding error in a payout is a real loss to a
 * real teenager.
 *
 * The currency travels with the amount, because "1000" means ten dollars and
 * one thousand yen and the number alone cannot tell you which.
 */
/** Decimal places each supported currency uses. ISO 4217 exponent. */
const EXPONENTS = {
    USD: 2,
    EUR: 2,
    GBP: 2,
    CAD: 2,
    AUD: 2,
    INR: 2,
    JPY: 0,
    KRW: 0,
    KWD: 3,
    BHD: 3,
};
export const DEFAULT_CURRENCY = 'USD';
export function currencyExponent(currency) {
    return EXPONENTS[currency.toUpperCase()] ?? 2;
}
export const currencyCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .length(3, 'Currency must be a 3-letter ISO 4217 code');
/**
 * An amount in minor units. Bounded well inside `Number.MAX_SAFE_INTEGER` so
 * it survives JSON without precision loss, and non-negative because nothing in
 * this product charges a negative amount.
 */
export const minorAmountSchema = z
    .number()
    .int('Amount must be a whole number of minor units')
    .min(0, 'Amount cannot be negative')
    .max(1_000_000_000_00, 'Amount is implausibly large');
/** An amount together with the currency it is denominated in. */
export const moneySchema = z.object({
    /** Integer count of the smallest unit, e.g. 2550 for $25.50. */
    amountMinor: minorAmountSchema,
    currency: currencyCodeSchema,
});
/**
 * Converts a human-entered major-unit value ("25.50") to minor units.
 *
 * Works on the string form to avoid binary-float drift: `25.5 * 100` is
 * `2550.0000000000005` in IEEE-754.
 */
export function toMinorUnits(major, currency = DEFAULT_CURRENCY) {
    const exponent = currencyExponent(currency);
    const text = typeof major === 'number' ? major.toFixed(exponent) : major.trim();
    if (!/^-?\d*(\.\d*)?$/.test(text) || text === '' || text === '.') {
        throw new Error(`Not a valid money amount: ${String(major)}`);
    }
    const negative = text.startsWith('-');
    const [whole = '0', fraction = ''] = text.replace('-', '').split('.');
    const padded = fraction.padEnd(exponent, '0').slice(0, exponent);
    // Round on the first dropped digit rather than truncating.
    const roundUp = exponent < fraction.length && Number(fraction[exponent]) >= 5;
    const minor = Number(`${whole}${padded}`) + (roundUp ? 1 : 0);
    return negative ? -minor : minor;
}
/** Minor units back to a plain major-unit string, e.g. 2550 -> "25.50". */
export function toMajorUnits(amountMinor, currency = DEFAULT_CURRENCY) {
    const exponent = currencyExponent(currency);
    if (exponent === 0)
        return String(amountMinor);
    const negative = amountMinor < 0;
    const digits = String(Math.abs(amountMinor)).padStart(exponent + 1, '0');
    const whole = digits.slice(0, -exponent);
    const fraction = digits.slice(-exponent);
    return `${negative ? '-' : ''}${whole}.${fraction}`;
}
/** Localised display, e.g. "$25.50". Formatting happens at the edge only. */
export function formatMoney(amountMinor, currency = DEFAULT_CURRENCY, locale) {
    const exponent = currencyExponent(currency);
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: exponent,
        maximumFractionDigits: exponent,
    }).format(amountMinor / 10 ** exponent);
}
//# sourceMappingURL=money.js.map